import type { UseQueryResult } from '@tanstack/react-query'
import type { ChartRegistryRow } from '~/api/endpoints/getCharts'

/** The chart_types PageTiles knows how to render. */
export const PAGE_TILE_TYPES = [
  'timeline',
  'absolute_bar',
  'summary_bar_strip',
  'metric_driver_bars',
  'dynamics_panels',
  'multi_timeframe_overlay',
] as const
export type PageTileType = (typeof PAGE_TILE_TYPES)[number]

export function isPageTileType(s: string): s is PageTileType {
  return (PAGE_TILE_TYPES as readonly string[]).includes(s)
}

export interface TileDescriptor {
  chart_type: PageTileType
  display_order: number
  key: string
}

export type PageGestalt =
  | { status: 'loading' }
  | { status: 'error'; error: Error }
  | { status: 'unknown'; unknown: string[] }
  | { status: 'ok'; tiles: TileDescriptor[] }

/** Pure: get_charts query -> ordered, de-duped, validated tile list. Loud on unknown chart_type. */
export function pageGestalt(charts: UseQueryResult<ChartRegistryRow[], Error>): PageGestalt {
  if (charts.isLoading) return { status: 'loading' }
  if (charts.error) return { status: 'error', error: charts.error }
  if (!charts.data) return { status: 'loading' }

  const unknown = [
    ...new Set(charts.data.map((r) => r.chart_type).filter((t) => !isPageTileType(t))),
  ]
  if (unknown.length > 0) return { status: 'unknown', unknown }

  const byType = new Map<PageTileType, TileDescriptor>()
  for (const r of charts.data) {
    if (!isPageTileType(r.chart_type)) continue
    const existing = byType.get(r.chart_type)
    if (!existing || r.display_order < existing.display_order) {
      byType.set(r.chart_type, {
        chart_type: r.chart_type,
        display_order: r.display_order,
        key: r.chart_type,
      })
    }
  }
  const tiles = [...byType.values()].sort((a, b) => a.display_order - b.display_order)
  return { status: 'ok', tiles }
}

/** chart_types whose tile implements the Columns (dim-value) fan-out. The control is hidden on a
 *  page with none of these (a dead control must disappear). */
export const COLUMNS_FANOUT_CHART_TYPES: readonly PageTileType[] = [
  'timeline',
  'absolute_bar',
  'metric_driver_bars',
]

export function pageSupportsColumns(gestalt: PageGestalt): boolean {
  return (
    gestalt.status === 'ok' &&
    gestalt.tiles.some((t) => COLUMNS_FANOUT_CHART_TYPES.includes(t.chart_type))
  )
}
